import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router';
import type { OfficeDashboardTileCountProps } from '@tempmee/core';
import { Row } from '@tempmee/react';
import { OfficeDashboardTile } from '@tempmee/react-blocks';
import {
  OutlineCheckCircleIcon,
  OutlineCircleIcon,
  OutlineCursorClickIcon,
} from '@tempmee/react-icons';
import { ConfirmCandidateSource } from '~/domain/ConfirmCandidateSource';
import { DashboardJobsStatus, useGetDashboardJobs } from '~/domain/DashboardJobs';
import { useAppSelector, useNavigateToConfirmCandidate } from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import { routes } from '~/routes';
import { CONFIRM_CANDIDATE_LABEL } from '~/utils/Enum';
import {
  fireDashboardActionNeededClicked,
  fireDashboardOpenShiftClicked,
  fireDashboardScheduledShiftClicked,
} from '~/posthogs/events';
import { SelectProfessionTypePopup } from '../ShiftRequestRedesign/SelectProfessionType/SelectProfessionTypePopup';
import { useSelectProfessionType } from '../ShiftRequestRedesign/SelectProfessionType/useSelectProfessionType';
import { BidNominationCard } from './BidNominationCard';
import { DashboardTileSkeleton } from './DashboardTileSkeleton';
import { PromoCodesCard } from './PromoCodesCard';

interface IShiftsSectionProps {
  isLoading?: boolean;
}

interface ICountTile extends Pick<
  OfficeDashboardTileCountProps,
  'title' | 'description' | 'accent' | 'icon' | 'count'
> {
  dataCy: string;
  isShown: boolean;
  onClick: () => void;
}

// The store's counts can arrive as numeric strings; anything that is not a
// positive number (null, NaN, a negative) is no shifts, never a printed "NaN".
const toCount = (value: unknown): number => {
  const count = parseInt(String(value ?? ''), 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const ShiftsSection = ({ isLoading }: IShiftsSectionProps) => {
  const navigate = useNavigate();
  const navigateToConfirmCandidate = useNavigateToConfirmCandidate();
  const dashboardInfo = useAppSelector((state) => state.job.dashboardInfo);
  const user = useUserSelector();
  const officeState = user?.address?.[0]?.state ?? '';

  const { availableProfessions } = useSelectProfessionType({ officeState });
  const [isShowPopup, setIsShowPopup] = useState(false);

  const { data, isLoading: isLoadingActionRequired } = useGetDashboardJobs({
    status: DashboardJobsStatus.ACTION_REQUIRED,
    page: 1,
    limit: 15,
  });

  const openShiftsCount = toCount(dashboardInfo?.activeRequest);
  const actionNeededCount = toCount(dashboardInfo?.actionRequired);
  const scheduledShiftsCount = toCount(dashboardInfo?.scheduled);
  const isLoadingCounts = isLoading || isLoadingActionRequired;

  const handleOpenShiftsClick = () => {
    fireDashboardOpenShiftClicked({ shiftCount: openShiftsCount });
    if (openShiftsCount === 0) {
      setIsShowPopup(true);
    } else {
      navigate(generatePath(routes.shiftsOverview, { type: 'open' }));
    }
  };

  const handleProfessionContinue = (professionCode: string) => {
    setIsShowPopup(false);
    navigate(routes.selectDates, { state: { professionType: professionCode } });
  };

  const handlePopupClose = () => {
    setIsShowPopup(false);
  };

  const handleActionNeededClick = () => {
    fireDashboardActionNeededClicked({ shiftCount: actionNeededCount });

    if (actionNeededCount === 1) {
      const counters = data?.[0]?.multipleCounters ?? [];
      const favoriteCandidate = counters.find((counter) => !!counter.isFavorite);

      const candidateId = (favoriteCandidate ?? counters?.[0])?.candidate?.id;
      const shiftId = data?.[0]?.id;

      if (candidateId && shiftId) {
        // Attribute a later confirm on this shift to the Action Needed card
        // (GOTU-4906) — same source as the Shifts Overview list, since both are
        // the office acting on their Action Needed queue.
        navigateToConfirmCandidate({
          shiftId,
          candidateId,
          source: ConfirmCandidateSource.ActionNeeded,
        });
        return;
      }
    }
    navigate(generatePath(routes.shiftsOverview, { type: 'pending' }));
  };

  const handleScheduledClick = () => {
    fireDashboardScheduledShiftClicked({ shiftCount: scheduledShiftsCount });
    navigate(generatePath(routes.shiftsOverview, { type: 'scheduled' }));
  };

  // Row order. Open shows at zero because it is how an office posts its first
  // shift; the others hide. While the counts load, a placeholder holds the slot
  // of each tile the last-known counts show — Open's alone on a first load.
  const countTiles: ICountTile[] = [
    {
      dataCy: 'action-needed-button',
      isShown: actionNeededCount > 0,
      title: CONFIRM_CANDIDATE_LABEL,
      description: 'Review before they expire',
      accent: 'flagged',
      icon: OutlineCursorClickIcon,
      count: actionNeededCount,
      onClick: handleActionNeededClick,
    },
    {
      dataCy: 'open-shifts-button',
      isShown: true,
      title: 'Open',
      description: openShiftsCount === 0 ? 'Click to post' : 'Invite to secure coverage',
      accent: 'primary',
      icon: OutlineCircleIcon,
      count: openShiftsCount,
      onClick: handleOpenShiftsClick,
    },
    {
      dataCy: 'scheduled-shifts-button',
      isShown: scheduledShiftsCount > 0,
      title: 'Scheduled',
      description: 'View Upcoming shift',
      accent: 'success',
      icon: OutlineCheckCircleIcon,
      count: scheduledShiftsCount,
      onClick: handleScheduledClick,
    },
  ];

  return (
    <>
      <Row gap={5} width="100%" wrap="wrap" data-cy="dashboard-tiles-row">
        {countTiles
          .filter(({ isShown }) => isShown)
          .map((tile) =>
            isLoadingCounts ? (
              <DashboardTileSkeleton key={tile.dataCy} />
            ) : (
              <OfficeDashboardTile
                key={tile.dataCy}
                title={tile.title}
                description={tile.description}
                accent={tile.accent}
                icon={tile.icon}
                count={tile.count}
                onClick={tile.onClick}
                className="flex-1"
                data-cy={tile.dataCy}
                data-count={tile.count}
              />
            ),
          )}

        <PromoCodesCard />

        <BidNominationCard />
      </Row>

      {isShowPopup && (
        <SelectProfessionTypePopup
          professions={availableProfessions}
          onContinue={handleProfessionContinue}
          onClose={handlePopupClose}
        />
      )}
    </>
  );
};
